import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const FAQSection = () => {
  const { isDarkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const faqs = [
    // ── Getting Started ───────────────────────────────────────────────────────
    {
      id: 1,
      category: 'Getting Started',
      question: 'How do I create my first form?',
      answer: 'From your Dashboard, click "+ Create New Form" in the top-right corner. Choose "Start from Scratch" to build with a blank canvas, or "Browse Templates" to start from a pre-built layout. Drag fields from the left panel onto the canvas, configure each field\'s label and settings in the right panel, then click "Publish" when you\'re ready to share it.'
    },
    {
      id: 2,
      category: 'Getting Started',
      question: 'Can I start from a template?',
      answer: 'Yes. Click "Browse Templates" on the Dashboard or navigate to Templates in the sidebar. Filter by category (Contact Forms, Surveys, Orders, Events, etc.), preview any template, and click "Use This Template" to open it in the builder. All fields, settings, and layout are fully editable.'
    },
    {
      id: 3,
      category: 'Getting Started',
      question: 'How do I share or publish my form?',
      answer: 'Click the "Publish" button in the Form Builder toolbar, toggle "Published" to on, then copy the direct link or embed code from the Share panel. Unpublished forms are only accessible to you; published forms are accessible to anyone with the link.'
    },
    // ── Form Building ─────────────────────────────────────────────────────────
    {
      id: 4,
      category: 'Form Building',
      question: 'How do I make a field required?',
      answer: 'Click the field on the canvas to open its settings in the right panel. Toggle "Required" on. The field will show a red asterisk to respondents and block form submission if left empty.'
    },
    {
      id: 5,
      category: 'Form Building',
      question: 'Can I show or hide fields based on earlier answers?',
      answer: 'Yes — this is called Conditional Logic. Select the field you want to conditionally show or hide, open its settings, and scroll to "Conditional Logic". Choose the source field, operator (equals, contains, is empty, etc.), the value to match, and the action (Show or Hide). For more complex multi-field rules, use the Logic panel in the toolbar.'
    },
    {
      id: 6,
      category: 'Form Building',
      question: 'What layout options are available?',
      answer: 'WebForms.biz supports four layouts: Standard (all fields on one scrollable page), Multi-Step (fields spread across pages with a progress bar), Conversational (one field at a time in a chat-style interface), and Split-Page (form with a branded image or content panel alongside it). Switch layouts any time via the Layout button in the toolbar.'
    },
    {
      id: 7,
      category: 'Form Building',
      question: 'Can I accept file uploads?',
      answer: 'Yes. Add a "File Upload" field to your form. Respondents can drag-and-drop or browse files up to 10 MB each. You can restrict accepted file types (e.g. images only, PDF only) in the field settings. Uploaded files are stored securely and downloadable from the Submissions view.'
    },
    // ── Integrations & Payments ───────────────────────────────────────────────
    {
      id: 8,
      category: 'Integrations',
      question: 'How do I accept payments through my form?',
      answer: 'Go to Integrations → Stripe and enter your Stripe Publishable Key. Then add a "Payment" field to your form, set the amount and currency, and publish. Respondents pay via Stripe\'s secure card form before the submission is saved. WebForms.biz never handles card numbers — all payment data goes directly to Stripe.'
    },
    {
      id: 9,
      category: 'Integrations',
      question: 'Where can I review form submissions?',
      answer: 'Every completed response is available in the Submissions dashboard. There you can search, filter, label, export, and review details before following up through your own preferred channel.'
    },
    {
      id: 10,
      category: 'Integrations',
      question: 'Can I embed my form on my own website?',
      answer: 'Yes. Go to Share / Embed in the builder toolbar. Options include: a direct shareable link, an iframe snippet (paste into your HTML), a JavaScript embed that auto-resizes as the form grows, and a popup/slide-in trigger. The JavaScript embed is recommended for multi-step and conversational forms.'
    },
    {
      id: 11,
      category: 'Integrations',
      question: 'Does WebForms.biz support webhooks?',
      answer: 'Yes. In the Form Builder, open Settings → Webhooks and add your endpoint URL. WebForms.biz sends a POST request with a JSON body containing the form name, submission ID, timestamp, and all field answers every time a form is submitted. Use webhooks to send data to Zapier, Make, your own backend, or any CRM.'
    },
    // ── Billing ───────────────────────────────────────────────────────────────
    {
      id: 12,
      category: 'Billing',
      question: 'What happens if I hit my plan\'s form or submission limit?',
      answer: 'If you reach your form limit, you can\'t create new forms (existing ones keep working). If you reach your monthly submission limit, new submissions are declined with a message to the respondent — your existing data is never deleted. Upgrade your plan any time to increase limits.'
    },
    {
      id: 13,
      category: 'Billing',
      question: 'Can I cancel my plan?',
      answer: 'Yes. Go to Account Settings → Billing and follow the cancellation steps. You keep access until the end of the current billing period. For refund or downgrade requests, contact support via the Help Center.'
    },
    {
      id: 14,
      category: 'Billing',
      question: 'What payment methods do you accept?',
      answer: 'All major credit and debit cards (Visa, Mastercard, American Express, Discover) processed securely via Stripe. For other payment arrangements, contact support.'
    },
    // ── Security & Privacy ────────────────────────────────────────────────────
    {
      id: 15,
      category: 'Security',
      question: 'Is my data secure?',
      answer: 'Yes. All data is encrypted in transit (TLS 1.2+) and at rest using industry-standard practices. Row-level security ensures each account can only access its own data. See our Privacy Policy for full details on data retention and deletion.'
    },
    {
      id: 16,
      category: 'Security',
      question: 'Can I add a consent checkbox to my form?',
      answer: 'Yes. Add a Checkbox field to your form and configure its label as a consent statement (e.g. "I agree to the Terms of Service"). Mark it as required so the form cannot be submitted without consent. All submitted values — including the checked state — appear in the Responses view.'
    },
  ];

  const categories = ['All', ...new Set(faqs.map(f => f.category))];

  const filtered = faqs.filter((faq) => {
    const matchesSearch = !searchQuery ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat = activeCategory === 'All' || faq.category === activeCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          placeholder="Search frequently asked questions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === cat
                ? isDarkMode
                  ? 'bg-[#10b981]/10 border border-[#10b981] text-[#10b981]'
                  : 'bg-[#107570]/10 border border-[#107570] text-[#107570]'
                : isDarkMode
                  ? 'bg-[#0f1410] border border-[#1a2820] hover:border-[#10b981]/50 text-foreground'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className={`p-12 rounded-lg border text-center ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-gray-50 border-gray-200'}`}>
            <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No FAQs found</h3>
            <p className="text-sm text-muted-foreground">Try different search terms or select a different category</p>
          </div>
        ) : (
          filtered.map((faq) => (
            <div
              key={faq.id}
              className={`rounded-lg border overflow-hidden transition-all ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-white border-gray-200'}`}
            >
              <button
                onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                className="w-full p-4 flex items-start justify-between gap-4 text-left hover:bg-accent/50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-[#10b981]/10 text-[#10b981]' : 'bg-[#107570]/10 text-[#107570]'}`}>
                      {faq.category}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground">{faq.question}</h3>
                </div>
                <Icon name={expandedId === faq.id ? 'ChevronUp' : 'ChevronDown'} size={20} className="text-muted-foreground flex-shrink-0 mt-1" />
              </button>
              {expandedId === faq.id && (
                <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-[#1a2820]' : 'border-gray-200'}`}>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Contact Support CTA */}
      <div className={`p-6 rounded-lg border text-center ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-gray-50 border-gray-200'}`}>
        <Icon name="MessageCircle" size={40} className="mx-auto mb-3 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Still have questions?</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Can't find the answer you're looking for? Submit a support ticket and we'll get back to you.
        </p>
      </div>
    </div>
  );
};

export default FAQSection;
